import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { EmptyState, PageHeader } from '@/components/page-header';
import { ReverseDialog } from '@/components/reverse-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { formatAmount, formatDate } from '@/lib/format';
import { sourceLabel } from '@/lib/journal';
import type { JournalEntry } from '@/lib/journal-api';
import { useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';

export const Route = createFileRoute('/accounting/journal-entries/')({ component: JournalEntries });

/** List page pattern (document 05) for screen A3 (document 06). */
function JournalEntries() {
  const orgId = useOrgId();
  const me = useMe();
  const isStaff = me.data?.isStaff ?? false;
  const navigate = useNavigate();
  const t = useT();
  const [reversing, setReversing] = useState<JournalEntry | null>(null);
  const query = useInfiniteQuery({
    queryKey: ['journal-entries', orgId],
    queryFn: ({ pageParam }) =>
      api<{ items: JournalEntry[]; nextCursor: string | null }>(
        `/orgs/${orgId}/journal-entries?limit=50${pageParam ? `&cursor=${pageParam}` : ''}`,
      ),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: Boolean(orgId),
  });
  const items = query.data?.pages.flatMap((p) => p.items) ?? [];
  const open = (id: string) =>
    void navigate({ to: '/accounting/journal-entries/$entryId', params: { entryId: id } });

  return (
    <>
      <PageHeader
        title={t('accountingPages.journalList.title')}
        actions={
          isStaff ? (
            <Button asChild>
              <Link to="/accounting/journal-entries/new">
                {t('accountingPages.journalList.newEntry')}
              </Link>
            </Button>
          ) : null
        }
      />
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
      ) : query.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : query.isError ? (
        <EmptyState title={t('accountingPages.journalList.loadError')} hint={query.error.message} />
      ) : items.length === 0 ? (
        <EmptyState
          title={t('accountingPages.journalList.emptyTitle')}
          hint={t('accountingPages.journalList.emptyHint')}
        />
      ) : (
        <>
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[110px]">
                    {t('accountingPages.journalList.colDate')}
                  </TableHead>
                  <TableHead className="w-[80px]">
                    {t('accountingPages.journalList.colNo')}
                  </TableHead>
                  <TableHead className="w-[140px]">
                    {t('accountingPages.journalList.colSource')}
                  </TableHead>
                  <TableHead>{t('accountingPages.journalList.colMemo')}</TableHead>
                  <TableHead className="w-[140px] text-end">
                    {t('accountingPages.journalList.colDebits')}
                  </TableHead>
                  <TableHead className="w-[140px] text-end">
                    {t('accountingPages.journalList.colCredits')}
                  </TableHead>
                  <TableHead className="w-[110px]">
                    {t('accountingPages.journalList.colStatus')}
                  </TableHead>
                  <TableHead className="w-[110px] text-end">
                    {t('accountingPages.journalList.colAction')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((e) => (
                  <TableRow key={e.id} className="cursor-pointer" onClick={() => open(e.id)}>
                    <TableCell>{formatDate(e.date)}</TableCell>
                    <TableCell className="tnum">{e.entryNo}</TableCell>
                    <TableCell>{sourceLabel(e.sourceType)}</TableCell>
                    <TableCell className="max-w-[420px] truncate">{e.memo ?? ''}</TableCell>
                    <TableCell className="tnum text-end">
                      {formatAmount(e.totalBaseDebit)}
                    </TableCell>
                    <TableCell className="tnum text-end">
                      {formatAmount(e.totalBaseCredit)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={e.status === 'reversed' ? 'secondary' : 'outline'}>
                        {e.status === 'reversed'
                          ? t('accountingPages.journalList.statusReversed')
                          : e.isAdjusting
                            ? t('accountingPages.journalList.statusAdjusting')
                            : t('accountingPages.journalList.statusPosted')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-end" onClick={(ev) => ev.stopPropagation()}>
                      <div className="inline-flex">
                        <Button
                          size="sm"
                          variant="link"
                          className="px-2"
                          onClick={() => open(e.id)}
                        >
                          {t('accountingPages.journalList.view')}
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="icon-sm"
                              variant="ghost"
                              aria-label={t('accountingPages.journalList.moreActions', {
                                no: e.entryNo,
                              })}
                            >
                              <ChevronDown className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              disabled={
                                !isStaff || e.status !== 'posted' || e.sourceType === 'reversal'
                              }
                              onSelect={() => setReversing(e)}
                            >
                              {t('accountingPages.journalList.reverse')}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={!isStaff}
                              onSelect={() =>
                                void navigate({
                                  to: '/accounting/journal-entries/new',
                                  search: { copyOf: e.id },
                                })
                              }
                            >
                              {t('accountingPages.journalList.copy')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {query.hasNextPage ? (
            <div className="mt-3 flex justify-center">
              <Button
                variant="outline"
                disabled={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {query.isFetchingNextPage
                  ? t('common.loading')
                  : t('accountingPages.journalList.loadMore')}
              </Button>
            </div>
          ) : null}
        </>
      )}
      <ReverseDialog entry={reversing} onClose={() => setReversing(null)} />
    </>
  );
}
